/**
 * 线索相关
 * @author tylerzzheng
 */
import { getInterfaceName, requestApiV2 } from '@library'
import {
  CommonList,
  LeadPoolFilter,
  ILeadPoolItem,
  NewLead,
  SourceTag,
  TouchStatus,
  UpdateLeadsFilter,
  AssignLeadsFilter,
  TouchStatusFilter,
  AddLeadBatchFilter,
  ReuseLeadFilter,
  FailLeadData,
  GetAddLeadCustomerFollower,
  CirculateRecordFilter, CirculateRecordItem,
  LeadTagsItem, LeadIntentionLevel, LeadTransferItem, LeadTransferListFilter, LeadIntentionAndPipelineItem, LeadIntentionAndPipelineListFilter, LeadIntentionAndPipelineByTagListFilter,
} from '@types'

/* 获取客户来源标签 */
export function getSourceTags () {
  return requestApiV2<SourceTag[]>('/trpc.tcc.tcc_main.Tag/GetSourceTag')
}

/* 获取触达状态 */
export function getTouchStatus (filter?: TouchStatusFilter) {
  return requestApiV2<CommonList<TouchStatus>>('/trpc.tcc.tcc_main.Lead/GetTouchStatus', filter)
}

/* 获取线索意向等级 https://opc-mock.woa.com/project/530/interface/api/3434 */
export function getLeadIntentionLevel () {
  return requestApiV2<LeadIntentionLevel[]>('/trpc.tcc.tcc_main.Lead/GetLeadIntentionLevel')
}

/* 添加线索 */
export function addLead (lead: NewLead) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/AddLead', lead)
}

/* 获取添加线索客户跟进人 http://tapd.oa.com/TC_CSIG_OM/markdown_wikis/show/#1220403412001797197 */
export function getLeadCustomerFollower (params: { name: string, uin: string, phone: string }) {
  return requestApiV2<GetAddLeadCustomerFollower>('/trpc.tcc.tcc_main.Lead/CheckLeadFollower', params)
}

/* 获取线索池列表 */
export function getLeadPoolList (leadFilter: LeadPoolFilter) {
  return requestApiV2<CommonList<ILeadPoolItem>>(getInterfaceName('/trpc.tcc.tcc_main.Lead/GetLeadList', leadFilter), leadFilter)
}

/**
 *  根据cid获取线索池列表，是全量非分页的
 *  http://tapd.oa.com/TC_CSIG_OM/markdown_wikis/show/#1220403412002013359
 *  */
export function getLeadListByCid (filter: {cid: string, excludeStatus?: number}) {
  return requestApiV2<ILeadPoolItem[]>('/trpc.tcc.tcc_main.Lead/GetLeadListByCid', filter)
}

/* 下载线索池列表 */
export function exportLeadList (leadFilter: LeadPoolFilter) {
  return requestApiV2<CommonList<ILeadPoolItem>>('/trpc.tcc.tcc_main.Lead/ExportLeadList', leadFilter)
}

/**
 * 更新线索
 * @param payload
 */
export function updateLeads (payload: UpdateLeadsFilter) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/UpdateLeads', payload)
}

/**
 * 分配线索 http://tapd.oa.com/TC_CSIG_OM/markdown_wikis/show/#1220403412000511503
 * @param payload
 */
export function assignLeads (payload: AssignLeadsFilter) {
  return requestApiV2<any>('/trpc.tcc.tcc_main.Lead/AssignLeads', payload)
}

// 获取线索流转记录
export function getLeadCirculateRecordList (params: CirculateRecordFilter) {
  return requestApiV2<CommonList<CirculateRecordItem>>(getInterfaceName('/trpc.tcc.tcc_main.Lead/GetLeadChangeRecordList', params), params)
}

/**
 * 获取线索标签 http://opc-mock.woa.com/project/530/interface/api/3383
 */
export function getLeadTags () {
  return requestApiV2<LeadTagsItem[]>('/trpc.tcc.tcc_main.Lead/GetLeadTags', { valid: true }) // 只允许选择现行有效的线索标签
}

/* 获取待转出意向线索列表 */
export function getLeadTransferList (leadTransferFilter: LeadTransferListFilter) {
  return requestApiV2<CommonList<LeadTransferItem>>(getInterfaceName('/trpc.tcc.tcc_main.Lead/GetSDRIntentionLeadList', leadTransferFilter), leadTransferFilter)
}

/* 下载待转出意向线索列表 */
export function exportLeadTransferList (leadTransferFilter: any) {
  return requestApiV2<CommonList<any>>('/trpc.tcc.tcc_main.Lead/ExportSDRIntentionLeadList', leadTransferFilter)
}

/* 获取意向和待孵化线索列表 */
export function getLeadIntentionAndPipelineList (leadIntentionFilter: LeadIntentionAndPipelineListFilter) {
  return requestApiV2<CommonList<LeadIntentionAndPipelineItem>>(getInterfaceName('/trpc.tcc.tcc_main.Lead/GetSDRLeadList', leadIntentionFilter), leadIntentionFilter)
}

/* 根据标签获取意向和待孵化线索列表 */
export function getSDRLeadListByTag (leadIntentionFilter: LeadIntentionAndPipelineByTagListFilter) {
  return requestApiV2<CommonList<LeadIntentionAndPipelineItem>>(getInterfaceName('/trpc.tcc.tcc_main.Lead/GetSDRLeadListByTag', leadIntentionFilter), leadIntentionFilter)
}

/* 下载意向和待孵化线索列表 */
export function exportLeadIntentionAndPipelineList (leadIntentionFilter: any) {
  return requestApiV2<CommonList<any>>('/trpc.tcc.tcc_main.Lead/ExportSDRLeadList', leadIntentionFilter)
}

export function addSDRIntentionLead (payload: any) {
  return requestApiV2<{ id: number }>('/trpc.tcc.tcc_main.Lead/AddSDRIntentionLead', payload)
}

export function getSDRIntentionLeadUploadPath () {
  return requestApiV2<{ path: string }>('/trpc.tcc.tcc_main.Lead/GetSDRIntentionLeadUploadPath')
}

export function getSDRIntentionLeadDownloadPath () {
  return requestApiV2<{ file: string }>('/trpc.tcc.tcc_main.Lead/GetSDRIntentionLeadUploadTemplate')
}

// 流转待转出意向线索
export function transferSDRIntentionLead (payload: { leadList: number[] }) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/TransferSDRIntentionLead', payload)
}

// 打回待转出意向线索
export function rejectSDRIntentionLead (payload: { intentionLeadId: number[] }) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/RepulseSDRIntentionLead', payload)
}

// 更新待转出意向线索
export function updateSDRIntentionLead (payload: any) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/UpdateSDRIntentionLead', payload)
}

// 获取待转出意向线索详情
export function getSDRIntentionLeadDetail (payload: { intentionLeadId: number }) {
  return requestApiV2<any>('/trpc.tcc.tcc_main.Lead/GetSDRIntentionLeadInfo', payload)
}

export function uploadSDRWeWorkLead (payload: { file: string }) {
  return requestApiV2<{}>('/trpc.tcc.tcc_main.Lead/UploadSDRWeWorkLead', payload)
}

// 获取市场部线索来源列表
export function getSDRLeadSourceList () {
  return requestApiV2<{ list: { sourceId: number; sourceName: string }[] }>('/trpc.tcc.tcc_main.Lead/GetSDRLeadSourceList')
}
